import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Descriptions,
  Select
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Create axios instance for admin API
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 10000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (process.env.NODE_ENV === 'development') {
      // Only use dummy token in development environment
      config.headers.Authorization = 'Bearer dummy-admin-token-for-testing';
      console.warn('DepositValidation: Using dummy token - DEVELOPMENT ONLY');
    } else {
      // In production, redirect to login if no token
      console.error('DepositValidation: No token found in production');
      window.location.href = '/admin/login';
      return Promise.reject(new Error('No authentication token'));
    }

    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      localStorage.removeItem('access_token');

      if (process.env.NODE_ENV === 'development') {
        console.log('DepositValidation: Token invalid, clearing localStorage and retrying with dummy token');
        // Retry request with dummy token only in development
        originalRequest.headers.Authorization = 'Bearer dummy-admin-token-for-testing';
      } else {
        console.log('DepositValidation: Token invalid in production, redirecting to login');
        window.location.href = '/admin/login';
        return Promise.reject(error);
      }

      return adminApi(originalRequest);
    }

    return Promise.reject(error);
  }
);

const DepositValidation = () => {

  const [loading, setLoading] = useState(false);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    fetchDepositData();
  }, [statusFilter]);

  const fetchDepositData = async () => {
    console.log('DepositValidation: Loading data from API...');
    setLoading(true);

    try {
      // Fetch deposit tickets and statistics in parallel
      const ticketsUrl = statusFilter === 'all'
        ? '/admin/deposit-tickets'
        : `/admin/deposit-tickets?status=${statusFilter}`;

      console.log('DepositValidation: Calling API with URL:', ticketsUrl);

      const [ticketsResponse, statsResponse] = await Promise.all([
        adminApi.get(ticketsUrl),
        adminApi.get('/admin/deposit-statistics')
      ]);

      console.log('DepositValidation: API Response received');
      console.log('Tickets Response:', ticketsResponse.data);
      console.log('Stats Response:', statsResponse.data);

      setPendingDeposits(ticketsResponse.data.tickets || []);
      setStats(statsResponse.data || {});

    } catch (error) {
      console.error('DepositValidation: Error fetching data:', error);
      message.error('Failed to load deposit data: ' + (error.response?.data?.error || error.message));
      setPendingDeposits([]);
      setStats({});
    } finally {
      setLoading(false);
      console.log('DepositValidation: Data loading completed');
    }
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      confirmed: 'green',
      rejected: 'red',
      expired: 'gray'
    };
    return colors[status] || 'default';
  };

  const handleValidateDeposit = (ticket) => {
    console.log('Opening validation modal for:', ticket.ticket_number);
    setSelectedTicket(ticket);
    form.setFieldsValue({
      amount: ticket.amount,
      reference_number: `REF${Date.now()}`,
      notes: ''
    });
    setValidationModalVisible(true);
  };

  const handleDetailView = async (ticket) => {
    console.log('Opening detail modal for:', ticket.ticket_number);
    setSelectedTicket(ticket);
    setDetailModalVisible(true);
  };

  const handleValidationSubmit = async (values) => {
    if (!selectedTicket) return;

    setValidationLoading(true);
    try {
      const response = await adminApi.post('/admin/validate-deposit', {
        ticket_id: selectedTicket.id,
        user_id: selectedTicket.user_id,
        amount: values.amount,
        reference_number: values.reference_number,
        notes: values.notes,
        action: 'confirm'
      });

      message.success(`Deposit berhasil dikonfirmasi! Saldo merchant bertambah: ${response.data.new_deposit_balance}`);
      setValidationModalVisible(false);
      form.resetFields();
      fetchDepositData(); // Refresh data

    } catch (error) {
      console.error('Validation error:', error);
      message.error('Gagal memvalidasi deposit: ' + (error.response?.data?.error || error.message));
    } finally {
      setValidationLoading(false);
    }
  };

  const handleRejectDeposit = async () => {
    if (!selectedTicket) return;

    setValidationLoading(true);
    try {
      await adminApi.post('/admin/validate-deposit', {
        ticket_id: selectedTicket.id,
        action: 'reject',
        notes: 'Deposit ditolak oleh admin'
      });

      message.success('Deposit berhasil ditolak');
      setValidationModalVisible(false);
      fetchDepositData(); // Refresh data

    } catch (error) {
      console.error('Rejection error:', error);
      message.error('Gagal menolak deposit: ' + (error.response?.data?.error || error.message));
    } finally {
      setValidationLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nomor Tiket',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      render: (text) => <Text copyable>{text}</Text>,
    },
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.full_name}</Text></div>
          <div><Text type="secondary">{record.member_id}</Text></div>
        </div>
      ),
    },
    {
      title: 'Jumlah',
      key: 'amount',
      render: (_, record) => (
        <div>
          <div><Text>Deposit: {formatCurrency(record.amount)}</Text></div>
          <div><Text strong style={{ color: '#52c41a' }}>Transfer: {formatCurrency(record.unique_amount)}</Text></div>
        </div>
      ),
    },
    {
      title: 'Bank',
      dataIndex: 'bank_account',
      key: 'bank_account',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Tanggal Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Tanggal Konfirmasi',
      dataIndex: 'confirmed_at',
      key: 'confirmed_at',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {record.status === 'pending' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={() => handleValidateDeposit(record)}
            >
              Validasi
            </Button>
          )}
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleDetailView(record)}
          >
            Detail
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Validasi Deposit Merchant</Title>
        <Text type="secondary">
          Kelola dan validasi tiket deposit merchant untuk top-up saldo
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Validasi"
              value={stats.pending_count || 0}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              suffix="tiket"
              valueStyle={{ color: '#faad14' }}
            />
            <Divider style={{ margin: '12px 0' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Total: {formatCurrency(stats.pending_amount || 0)}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Dikonfirmasi Hari Ini"
              value={stats.today_confirmed || 0}
              prefix={<CheckOutlined style={{ color: '#52c41a' }} />}
              suffix="tiket"
              valueStyle={{ color: '#52c41a' }}
            />
            <Divider style={{ margin: '12px 0' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Total: {formatCurrency(stats.today_confirmed_amount || 0)}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Expired/Kadaluarsa"
              value={stats.total_expired || 0}
              prefix={<WarningOutlined style={{ color: '#ff4d4f' }} />}
              suffix="tiket"
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Semua Status"
              value={(stats.pending_count || 0) + (stats.today_confirmed || 0) + (stats.total_expired || 0)}
              prefix={<HistoryOutlined style={{ color: '#1890ff' }} />}
              suffix="tiket"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Deposits Table */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>Tiket Deposit</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Text>Status:</Text>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 120 }}
                size="small"
              >
                <Select.Option value="all">Semua</Select.Option>
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="confirmed">Confirmed</Select.Option>
                <Select.Option value="rejected">Rejected</Select.Option>
                <Select.Option value="expired">Expired</Select.Option>
              </Select>
            </div>
          </div>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchDepositData}
            loading={loading}
          >
            Refresh
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={pendingDeposits}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} tiket`
          }}
        />
      </Card>

      {/* Validation Modal */}
      <Modal
        title={`Validasi Deposit - ${selectedTicket?.ticket_number}`}
        open={validationModalVisible}
        onCancel={() => setValidationModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleValidationSubmit}
        >
          <Form.Item label="Informasi Merchant">
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Nama">{selectedTicket?.full_name}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedTicket?.email}</Descriptions.Item>
              <Descriptions.Item label="Bank">{selectedTicket?.bank_account}</Descriptions.Item>
              <Descriptions.Item label="Amount">{formatCurrency(selectedTicket?.amount)}</Descriptions.Item>
              <Descriptions.Item label="Unique Amount">{formatCurrency(selectedTicket?.unique_amount)}</Descriptions.Item>
            </Descriptions>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Jumlah"
            rules={[{ required: true, message: 'Jumlah wajib diisi' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/Rp\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="reference_number"
            label="Reference Number"
            rules={[{ required: true, message: 'Reference number wajib diisi' }]}
          >
            <Input placeholder="Masukkan reference number" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Catatan"
          >
            <Input.TextArea rows={3} placeholder="Catatan validasi (opsional)" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setValidationModalVisible(false)}
              disabled={validationLoading}
            >
              Batal
            </Button>
            <Button
              danger
              onClick={handleRejectDeposit}
              loading={validationLoading}
            >
              Tolak
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={validationLoading}
            >
              Konfirmasi
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`Detail Deposit - ${selectedTicket?.ticket_number}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Tutup
          </Button>
        ]}
        width={700}
      >
        {selectedTicket && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Nomor Tiket" span={2}>
              <Tag color="blue">{selectedTicket.ticket_number}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Nama Merchant">{selectedTicket.full_name}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedTicket.email}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedTicket.phone}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedTicket.status)}>
                {selectedTicket.status?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">{formatCurrency(selectedTicket.amount)}</Descriptions.Item>
            <Descriptions.Item label="Unique Amount">{formatCurrency(selectedTicket.unique_amount)}</Descriptions.Item>
            <Descriptions.Item label="Bank Account" span={2}>{selectedTicket.bank_account}</Descriptions.Item>
            <Descriptions.Item label="Created At">
              {dayjs(selectedTicket.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Expires At">
              {dayjs(selectedTicket.expires_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            {selectedTicket.confirmed_at && (
              <Descriptions.Item label="Confirmed At" span={2}>
                {dayjs(selectedTicket.confirmed_at).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Notes" span={2}>
              {selectedTicket.notes || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default DepositValidation;